import { Link } from "react-router";
import type { HomeworkStateFilter, StudentSummary } from "@edu/shared";
import { SegmentedRadio } from "../../../lib/SegmentedRadio.tsx";
import type { HomeworkListCounts } from "./use-homework-list.ts";

/**
 * Разрез по ученику, фильтр состояния и переключатель последней выдачи (5.2
 * фазы 2).
 *
 * ⚠️ Разрез пишется в АДРЕС, а не в состояние компонента: адрес это то, что
 * пересылают и открывают в соседней вкладке, и это же то, куда возвращается
 * карточка работы после «В корзину».
 *
 * ⚠️ Фильтр состояния серверный (параметр `?state=`), поэтому подписи «отобрано
 * среди загруженных N» здесь нет и быть не должно: список полон по определению
 * фильтра, а такая подпись означала бы обратное.
 *
 * ⚠️ **Своего вида у полосы больше нет** (14.09.2026, подход 2е переноса
 * дизайн-системы). Карточка вокруг ряда снята, свои чипы фильтра состояния
 * заменены общим сегментом, стрелки и перепрыгивание выключенных сегментов
 * уехали в `lib/SegmentedRadio.tsx` вместе с разметкой. Здесь остался состав
 * вопросов к списку и ничего сверх него.
 */

/**
 * `count` это ключ в счётчиках ответа. Ключ лежит рядом со значением фильтра, а
 * не выводится из него в месте отрисовки: `not_done` и `notDone` это разные
 * написания одного смысла, и сопоставлять их тернаром в разметке значит
 * заводить второе место, знающее про пары.
 */
const STATES: readonly {
  value: HomeworkStateFilter;
  label: string;
  count: keyof HomeworkListCounts;
}[] = [
  { value: "all", label: "Все", count: "all" },
  { value: "not_done", label: "Не сдано", count: "notDone" },
  { value: "overdue", label: "Просрочено", count: "overdue" },
  { value: "done", label: "Сдано", count: "done" },
];

export function HubFilters(props: {
  /** `null` означает «список учеников ещё грузится». */
  students: StudentSummary[] | null;
  studentUserId: string | null;
  onStudent: (studentUserId: string | null) => void;
  state: HomeworkStateFilter;
  onState: (state: HomeworkStateFilter) => void;
  /**
   * Числа у сегментов. `null` означает «сервер их не прислал» (старый API либо
   * ещё летящий первый запрос): сегменты тогда остаются без чисел, а не с
   * нулями. Ноль от отсутствия работ и ноль от отсутствия счётчиков выглядят
   * одинаково, и первый показывать надо, а второй врёт.
   */
  counts: HomeworkListCounts | null;
  latestOnly: boolean;
  onLatestOnly: (value: boolean) => void;
  /** Число работ последней раздачи среди приехавших. 0 означает «раздач нет». */
  latestCount: number;
  busy: boolean;
}) {
  const students = props.students ?? [];
  const picked = students.find((s) => s.userId === props.studentUserId) ?? null;
  const hasBatch = props.latestCount > 0;

  /*
    Выключение живёт одной функцией, потому что его спрашивают дважды: разметка
    у сегмента и стрелки, когда ищут, куда шагнуть. Разъехавшись, эти два места
    дали бы стрелку, которая выбирает то, что мышью нажать нельзя.

    Пока летит запрос, выключены все сегменты, кроме выбранного: список ещё
    отвечает на прошлый вопрос, и второй щелчок в этот момент означал бы гонку
    двух ответов за один список.
  */
  const segDisabled = (value: HomeworkStateFilter) => props.busy && props.state !== value;

  return (
    /*
      ⚠️ **Полоса, а не карточка** (14.09.2026). Разрез по ученику, фильтр
      состояния и переключатель последней выдачи это три вопроса к одному и тому
      же списку, и стоят они одним рядом над ним. Коробки у ряда нет: карточка
      это БЛОК экрана, а две коробки подряд (полоса и таблица под ней) читаются
      как два раздела там, где раздел один. Разбор у `.bar` в `styles.css`.

      ⚠️ Трёх внутренних строк (`.hub__row`) тоже больше нет: ряд переносится
      сам по `flex-wrap`, и на телефоне встаёт теми же тремя строками, а на
      ноутбуке одной.
    */
    <section className="bar">
      {/*
        ⚠️ Подписи словом у разреза нет, и это не потеря: первый пункт списка
        называет шкалу сам («Все ученики»), а читалке она названа `aria-label`.
        Слово «Ученик» перед полем повторяло бы то, что стоит внутри него, и
        так же устроен тот же фильтр в архиве занятий.
      */}
      <select
        className="fld"
        value={props.studentUserId ?? ""}
        disabled={props.students === null}
        aria-label="Ученик"
        onChange={(e) => props.onStudent(e.target.value === "" ? null : e.target.value)}
      >
        <option value="">Все ученики</option>
        {students.map((s) => (
          <option key={s.userId} value={s.userId}>
            {s.displayName}
            {s.unlinked ? " (отвязан)" : ""}
          </option>
        ))}
      </select>

      {/*
        Карточка ученика перестала быть обязательной остановкой, но осталась
        коротким путём: ссылка отсюда туда, а не оттуда сюда (1.8 фазы 2)
      */}
      {picked !== null && (
        <>
          <Link className="textlink" to={`/students/${picked.userId}`}>
            карточка ученика
          </Link>
          {!picked.unlinked && (
            <Link className="textlink" to={`/homework/new?studentId=${picked.userId}`}>
              задать ему домашку
            </Link>
          )}
        </>
      )}

      {/*
        ⚠️ Фильтр состояния это выбор ОДНОГО из четырёх, значит сегмент, и
        никакого своего вида ему не полагается (подход 2е переноса). До
        14.09.2026 он был написан руками чипами, и довод стоял прямо здесь:
        скользящая подложка сегмента требовала равной ширины, четыре сегмента по
        «Просрочено» это около 28rem, и в телефон это не влезало. Подложки
        больше нет, ряд переносится, довод вместе с ней отпал.

        Ноль стоит числом, а сегмент не прячется: «Просрочено · 0» это ответ на
        вопрос, ради которого сюда пришли, а исчезнувший сегмент это вопрос без
        ответа.
      */}
      <SegmentedRadio
        ariaLabel="Состояние домашек"
        segments={STATES.map((s) => ({
          id: s.value,
          label: s.label,
          count: props.counts === null ? null : props.counts[s.count],
          disabled: segDisabled(s.value),
        }))}
        value={props.state}
        onPick={(id) => props.onState(id as HomeworkStateFilter)}
      />

      {/*
        ⚠️ Подпись различается по разрезу, и это не придирка: в общем списке это
        «моя последняя раздача», а в разрезе по ученику «последняя раздача, в
        которую попал он». Одна подпись на два смысла давала бы уверенность, что
        смотришь на вчерашнюю раздачу двенадцати, когда смотришь на позавчерашнюю
        раздачу троим.

        Переключатель работает по уже приехавшим страницам, поэтому при пустом
        списке он ВЫКЛЮЧЕН и подписан «таких ещё нет»: пустой список читается
        как «работ нет», выключенный переключатель как «нечего фильтровать».
      */}
      <label className="form__check">
        <input
          type="checkbox"
          checked={props.latestOnly && hasBatch}
          disabled={!hasBatch}
          onChange={(e) => props.onLatestOnly(e.target.checked)}
        />{" "}
        {!hasBatch
          ? "только последние, заданные вместе: таких ещё нет"
          : props.studentUserId === null
            ? `только последние, заданные вместе (${props.latestCount})`
            : `только последние, заданные этому ученику (${props.latestCount})`}
      </label>
    </section>
  );
}
